// ROI summary step (stories 64-69). Program value and Clay-attributed value at equal size, the exact
// equation label under them, ROI as attributed value over the contract estimate, pipeline as a
// secondary line. Strictly read-only for realization and attribution: those are decided on the use
// case pages and in review, not haggled here (story 69). The one editable thing is the Clay contract
// estimate, an inline input in the ROI tile (round-1 decision 15). A bar chart of Clay-attributed
// value by use case (decision 18) sits above the per-use-case table.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { useDraftState } from '../../../_shared/hooks';
import { numberToInput, parseNumberInput } from '../../flow/number-input';
import { NumberField } from './NumberField';
import { ConnectorPaths, snakeConnector, type Connector } from './FormulaChain';
import { DEAL_STEP, SELECTION_STEP, stepIdForUseCase } from '../../flow/step-model';
import { calculatorActions, sizingOf, useCalculatorStore } from '../../state/calculator-store';
import { iconUrl } from '../../data/icons';
import { Ph } from '../Icon';
import { TOTAL_ATTRIBUTED_LABEL } from './attributed-label';
import { slabStyle } from './slab';
import { buildReviewVm } from './review-model';
import { buildRoiSummaryVm } from './roi-summary-model';

const MISSING_COPY: Record<string, string> = {
  currentAnnualPipeline:
    'Current annual pipeline is needed for Account / lead scoring. That use case counts as $0 until you enter it.',
};

/**
 * The Clay contract estimate, in the same big input card as deal economics, as the divisor of the
 * ROI row. Blank by default and never pre-filled; every keystroke pushes the parsed number to the
 * store so ROI updates live. Draft text is seeded once per store `epoch` (root CLAUDE.md "Draft State
 * Under SSE").
 */
function ContractEstimateCard() {
  const epoch = useCalculatorStore((s) => s.epoch);
  const estimate = useCalculatorStore((s) => s.deal.contractEstimate);
  const seed = { text: numberToInput(estimate) };
  const { draft, setDraft } = useDraftState<{ text: string }>(seed, epoch);
  const text = (draft ?? seed).text;
  return (
    <div className="vc3-dcard vc3-roi-contract">
      <label className="vc3-dcard-label" htmlFor="vc3-roi-estimate">
        Clay contract value (annual)
      </label>
      <div className="vc3-dcard-field">
        <span aria-hidden>$</span>
        <NumberField
          id="vc3-roi-estimate"
          className="vc3-dcard-input"
          {...AUTOFILL_IGNORE_PROPS}
          placeholder="0"
          value={text}
          onChange={(t) => {
            setDraft({ text: t });
            calculatorActions.setDeal({ contractEstimate: parseNumberInput(t) });
          }}
        />
      </div>
      <span className="vc3-dcard-help">Blank until you have an estimate.</span>
    </div>
  );
}

/**
 * The chain's snake connector, reused: out of the right of row 1's Clay-attributed slab, through the
 * band under the pills, and into the ÷ that opens row 2, so the attributed value reads as the
 * dividend without repeating its card. Re-measured whenever the equation grid changes size.
 */
function RoiSnake({ grid }: { grid: HTMLDivElement | null }) {
  const [paths, setPaths] = useState<Connector[]>([]);
  useEffect(() => {
    if (!grid || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const from = grid.querySelector<HTMLElement>('[data-snake-from]');
      const lead = grid.querySelector<HTMLElement>('[data-lead]');
      const under = grid.querySelector<HTMLElement>('.vc3-roi-under');
      const next = grid.querySelector<HTMLElement>('.vc3-roi-contract');
      const c =
        from && lead && under && next
          ? snakeConnector(
              grid.getBoundingClientRect(),
              from.getBoundingClientRect(),
              lead,
              lead.getBoundingClientRect().left,
              (under.getBoundingClientRect().bottom + next.getBoundingClientRect().top) / 2,
            )
          : null;
      const nextPaths = c ? [c] : [];
      setPaths((prev) => (JSON.stringify(prev) === JSON.stringify(nextPaths) ? prev : nextPaths));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    grid.querySelectorAll(':scope > *').forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [grid]);
  return <ConnectorPaths paths={paths} />;
}

/**
 * One value of the per-use-case equation, in the colour of its card on the use case page: gross is
 * the lime result card, realization / attribution the white input cards (dashed lemon while on the
 * default, as Assumed), program value the bold program card and Clay-attributed the bold slushie one.
 * The muted operator sits to its left, so the row reads gross × realization = program × attribution
 * = Clay-attributed.
 */
function ValuePill({
  kind,
  op,
  assumed,
  children,
}: {
  kind: 'gross' | 'factor' | 'program' | 'attributed';
  op?: '×' | '=';
  assumed?: boolean;
  children: ReactNode;
}) {
  return (
    <span className="vc3-roi-eq">
      {op && (
        <span className="vc3-roi-eq-op" aria-hidden>
          {op}
        </span>
      )}
      <span className="vc3-roi-vpill" data-kind={kind} data-assumed={assumed ? true : undefined}>
        {children}
      </span>
    </span>
  );
}

export function RoiSummaryStep() {
  const [grid, setGrid] = useState<HTMLDivElement | null>(null);
  const sizing = useCalculatorStore(useShallow(sizingOf));
  const customerName = useCalculatorStore((s) => (s.account?.kind === 'account' ? s.account.accountName : ''));
  const vm = buildRoiSummaryVm(sizing);
  const assumedTotal = vm.empty ? 0 : buildReviewVm(sizing, []).assumedTotal;

  return (
    <section aria-labelledby="vc3-roi-h">
      <div className="vc3-eyebrow">ROI summary</div>
      <h1 id="vc3-roi-h" className="vc3-h1">
        {customerName ? `What Clay is worth to ${customerName}` : 'What Clay is worth'}
      </h1>

      {vm.empty ? (
        <div className="vc3-banner" role="status">
          <span>No use cases are selected yet, so there is no number to show.</span>
          <button type="button" className="vc3-linkbtn" onClick={() => calculatorActions.goToStep(SELECTION_STEP)}>
            Choose use cases
          </button>
        </div>
      ) : (
        <>
          {/* Two equations on one grid, each tile a solid slab:
              program value × attribution = Clay-attributed value, then (the snake carrying the
              attributed value down) ÷ contract value = ROI. */}
          <div className="vc3-roi-equation" role="group" aria-label={vm.formulaLabel} ref={setGrid}>
            <RoiSnake grid={grid} />
            <div className="slab vc3-roi-tile vc3-roi-tile-program" style={slabStyle('lime', 300)}>
              <div className="vc3-roi-tile-label">Total program value</div>
              <div className="vc3-roi-tile-val">{vm.programText}</div>
              <div className="vc3-roi-tile-sub">
                per year, across {vm.useCaseCount} use case{vm.useCaseCount === 1 ? '' : 's'}
              </div>
              <img className="vc3-roi-tile-icon" src={iconUrl('assets/icons/Growth-chart.png')} alt="" aria-hidden />
            </div>
            <span className="vc3-roi-op" aria-hidden>
              ×
            </span>
            <div className="slab vc3-roi-tile vc3-roi-factor" style={slabStyle('oat', 200)}>
              <div className="vc3-roi-tile-label">Attribution factor</div>
              <div className="vc3-roi-factor-val">{vm.attributionText}</div>
              <div className="vc3-roi-tile-sub">varies by use case</div>
            </div>
            <span className="vc3-roi-op" aria-hidden>
              =
            </span>
            <div className="slab vc3-roi-tile vc3-roi-tile-accent" data-snake-from="" style={slabStyle('slushie', 200)}>
              <div className="vc3-roi-tile-label">{TOTAL_ATTRIBUTED_LABEL}</div>
              <div className="vc3-roi-tile-val">{vm.attributedText}</div>
              <div className="vc3-roi-tile-sub">per year</div>
            </div>

            {/* Under the program value slab: gross, then pipeline. */}
            <div className="vc3-roi-under">
              <span className="slab vc3-roi-pill" style={slabStyle('lemon', 200)}>
                Gross {vm.grossText} before realization
              </span>
              {vm.pipelineText && (
                <span className="slab vc3-roi-pill" style={slabStyle('ube', 200)}>
                  {vm.pipelineText}
                </span>
              )}
            </div>

            {/* Row 2 opens on the ÷ the snake lands on; its first column stays empty. */}
            <span className="vc3-roi-row2-gap" aria-hidden />
            <span className="vc3-roi-op" data-lead="" aria-hidden>
              ÷
            </span>
            <ContractEstimateCard />
            <span className="vc3-roi-op" aria-hidden>
              =
            </span>
            <div className="slab vc3-roi-tile vc3-roi-roi" data-state={vm.roi.kind} style={slabStyle('lemon', 300)}>
              <div className="vc3-roi-tile-label">ROI</div>
              {vm.roi.kind === 'roi' ? (
                <>
                  <div className="vc3-roi-tile-val">{vm.roi.text}</div>
                  <div className="vc3-roi-tile-sub">{vm.roi.basisText}</div>
                </>
              ) : (
                <div className="vc3-roi-absent">{vm.roi.copy}</div>
              )}
            </div>
          </div>

          {vm.missingInputs.map((m) => (
            <div key={m} className="vc3-banner" role="alert">
              <Ph name="info" size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{MISSING_COPY[m] ?? `${m} is missing.`}</span>
              <button type="button" className="vc3-linkbtn" onClick={() => calculatorActions.goToStep(DEAL_STEP)}>
                Enter it
              </button>
            </div>
          ))}

          <section className="slab vc3-roi-chart" style={slabStyle('oat', 200)} aria-labelledby="vc3-roi-chart-h">
            <h2 id="vc3-roi-chart-h" className="vc3-roi-chart-h">
              Clay-attributed value by use case
            </h2>
            <ul className="vc3-roi-bars">
              {vm.bars.map((b) => (
                <li key={b.id} className="vc3-roi-bar-row">
                  <span className="vc3-roi-bar-name">{b.name}</span>
                  <span className="vc3-roi-bar-track" style={{ '--w': b.widthPct / 100 } as CSSProperties}>
                    <span
                      className="vc3-roi-bar"
                      data-tier={b.tier}
                      role="img"
                      aria-label={`${b.name}: ${b.valueText} ${b.valueLabel}, ${b.programText} program value`}
                      title={`${b.valueLabel} ${b.valueText}`}
                    />
                    <span className="vc3-roi-bar-val">
                      {b.valueText}
                      <span className="vc3-roi-bar-prog"> of {b.programText} program</span>
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="vc3-roi-legend" aria-hidden>
              {(['Crawl', 'Walk', 'Run', 'Efficiency'] as const).map((t) => (
                <span key={t}>
                  <i data-tier={t} /> {t}
                </span>
              ))}
            </p>
          </section>

          <div className="slab vc3-roi-table-wrap" style={slabStyle('oat', 200)}>
            <table className="vc3-roi-table">
              <caption className="vc3-roi-caption">
                Realization and attribution are read-only here. Set them on each use case page or in the review.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Use case</th>
                  <th scope="col">Gross</th>
                  <th scope="col">Realization</th>
                  <th scope="col">Program value</th>
                  <th scope="col">Attribution</th>
                  <th scope="col">Clay-attributed</th>
                </tr>
              </thead>
              <tbody>
                {vm.lines.map((l) => (
                  <tr key={l.id}>
                    <th scope="row">
                      <button
                        type="button"
                        className="vc3-linkbtn"
                        onClick={() => calculatorActions.goToStep(stepIdForUseCase(l.id))}
                      >
                        {l.name}
                      </button>
                      <span className="vc3-tierbadge" data-tier={l.tier}>
                        {l.tier}
                      </span>
                    </th>
                    <td>
                      <ValuePill kind="gross">{l.grossText}</ValuePill>
                    </td>
                    <td>
                      <ValuePill kind="factor" op="×" assumed={l.realizationAssumed}>
                        {l.realizationText}
                      </ValuePill>
                    </td>
                    <td>
                      <ValuePill kind="program" op="=">
                        {l.programText}
                      </ValuePill>
                    </td>
                    <td>
                      <ValuePill kind="factor" op="×" assumed={l.attributionAssumed}>
                        {l.attributionText}
                      </ValuePill>
                    </td>
                    <td className="vc3-roi-attrcell">
                      <ValuePill kind="attributed" op="=">
                        {l.attributedText}
                      </ValuePill>
                      <span className="vc3-roi-col-sub">{l.attributedLabel}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {assumedTotal > 0 && (
            <div className="vc3-banner" role="note" style={{ marginTop: 16 }}>
              <Ph name="info" size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                {assumedTotal} {assumedTotal === 1 ? 'input is' : 'inputs are'} still on Clay defaults and will
                show as assumed in the deck.
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
